import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  SwapOutlined,
  HeartOutlined,
  HeartFilled,
  LoadingOutlined,
  PauseOutlined,
  CaretRightOutlined,
  CustomerServiceOutlined,
  SoundOutlined,
  AudioMutedOutlined,
  RetweetOutlined,
  StepBackwardOutlined,
  StepForwardOutlined,
} from '@ant-design/icons'
import { useAudioPlayer } from '../../contexts/AudioPlayerContext'
import { useAppDispatch, useAppSelector } from '../../utils/redux/store'
import {
  attemptAddSongToFavs,
  attemptDeleteSongFromFavs,
  attemptGetFavs,
} from '../../utils/redux/favsSlice'
import './GlobalAudioPlayer.scss'

type RepeatMode = 'off' | 'all' | 'one'

const LS_SHUFFLE = 'musictmm.player.shuffle'
const LS_REPEAT = 'musictmm.player.repeat'
const LS_VOLUME = 'musictmm.player.volume'
const LS_MUTED = 'musictmm.player.muted'

function lsGet(key: string) {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}
function lsSet(key: string, val: string) {
  try {
    localStorage.setItem(key, val)
  } catch {}
}

function clamp01(v: number) {
  if (Number.isNaN(v)) return 0
  return Math.max(0, Math.min(1, v))
}

// Fisher–Yates
function fisherYatesShuffle<T>(arr: T[]): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function sigOf(q: any[]) {
  return (q || []).map((s: any) => String(s?._id ?? '')).join('|')
}

export const GlobalAudioPlayer: React.FC = () => {
  const audioPlayer = useAudioPlayer()
  const {
    currentSong,
    queue,
    currentIndex,
    isPlaying,
    currentTime,
    duration,
    isLoading,
    togglePlayPause,
    seek,
    playQueue,
  } = audioPlayer

  const dispatch = useAppDispatch()
  const favSongs = useAppSelector((s: any) => s.favs?.songs ?? [])

  const [queueOpen] = useState(false)

  // persist playback prefs
  const [shuffle, setShuffle] = useState(() => lsGet(LS_SHUFFLE) === '1')
  const [repeatMode, setRepeatMode] = useState<RepeatMode>(() => {
    const v = lsGet(LS_REPEAT) as RepeatMode | null
    return v === 'one' ? 'one' : 'off'
  })

  // volume prefs
  const [volume, setVolume] = useState<number>(() => {
    const raw = lsGet(LS_VOLUME)
    const parsed = raw == null ? 0.8 : Number(raw)
    return Number.isFinite(parsed) ? clamp01(parsed) : 0.8
  })
  const [muted, setMuted] = useState<boolean>(() => lsGet(LS_MUTED) === '1')

  const [isMobile, setIsMobile] = useState(false)
  const desktopPlayerRef = useRef<HTMLDivElement | null>(null)

  const repeatRef = useRef<RepeatMode>(repeatMode)
  const shuffleRef = useRef<boolean>(shuffle)
  const isPlayingRef = useRef<boolean>(isPlaying)

  useEffect(() => {
    repeatRef.current = repeatMode
  }, [repeatMode])
  useEffect(() => {
    shuffleRef.current = shuffle
  }, [shuffle])
  useEffect(() => {
    isPlayingRef.current = isPlaying
  }, [isPlaying])

  const autoGuardRef = useRef<{ songId?: string; stamp?: number }>({})

  // -------------------------
  // Shuffle engine
  // -------------------------
  const shufflePoolRef = useRef<string[]>([])
  const shuffleHistoryRef = useRef<string[]>([])
  const shuffleFutureRef = useRef<string[]>([])
  const shuffleSigRef = useRef<string>('')
  const navCauseRef = useRef<'next' | 'prev' | null>(null)
  const lastSongIdRef = useRef<string>('')

  useEffect(() => {
    dispatch(attemptGetFavs())
  }, [dispatch])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(max-width: 900px)')
    const onChange = () => setIsMobile(mq.matches)
    onChange()
    mq.addEventListener?.('change', onChange)
    return () => mq.removeEventListener?.('change', onChange)
  }, [])

  // ✅ Десктопный отступ для страниц (чтобы контент/кнопки не залезали под fixed player)
  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement

    const setDesktopOffset = () => {
      if (isMobile || !currentSong) {
        root.style.setProperty('--ga-desktop-offset', '0px')
        return
      }

      const el = desktopPlayerRef.current
      if (!el) {
        root.style.setProperty('--ga-desktop-offset', '116px')
        return
      }

      const rect = el.getBoundingClientRect()
      const cs = window.getComputedStyle(el)
      const bottom = Number.parseFloat(cs.bottom || '0') || 0
      // запас 12px чтобы не прилипало
      const offset = Math.ceil(rect.height + bottom + 12)
      root.style.setProperty('--ga-desktop-offset', `${offset}px`)
    }

    setDesktopOffset()

    let ro: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined' && desktopPlayerRef.current && !isMobile) {
      ro = new ResizeObserver(() => setDesktopOffset())
      ro.observe(desktopPlayerRef.current)
    }

    window.addEventListener('resize', setDesktopOffset)

    return () => {
      ro?.disconnect()
      window.removeEventListener('resize', setDesktopOffset)
      root.style.setProperty('--ga-desktop-offset', '0px')
    }
  }, [isMobile, currentSong, queueOpen])

  const safeQueue = useMemo(
    () => (queue?.length ? queue : currentSong ? [currentSong] : []),
    [queue, currentSong],
  )

  const safeIds = useMemo(
    () => safeQueue.map((s: any) => String(s?._id ?? '')).filter(Boolean),
    [safeQueue],
  )

  const queueSig = useMemo(() => sigOf(safeQueue), [safeQueue])

  const currentSongId = String((currentSong as any)?._id ?? '')

  const realIndex = useMemo(() => {
    if (!safeQueue.length || !currentSongId) return Math.max(0, currentIndex ?? 0)
    const idx = safeQueue.findIndex((s: any) => String(s?._id ?? '') === currentSongId)
    return idx >= 0 ? idx : Math.max(0, currentIndex ?? 0)
  }, [safeQueue, currentSongId, currentIndex])

  // persist settings
  useEffect(() => {
    lsSet(LS_SHUFFLE, shuffle ? '1' : '0')
  }, [shuffle])

  useEffect(() => {
    lsSet(LS_REPEAT, repeatMode)
  }, [repeatMode])

  useEffect(() => {
    lsSet(LS_VOLUME, String(clamp01(volume)))
  }, [volume])

  useEffect(() => {
    lsSet(LS_MUTED, muted ? '1' : '0')
  }, [muted])

  // apply volume to player (with fallbacks)
  const applyOutputVolume = useCallback(
    (nextVolume: number, nextMuted: boolean) => {
      const effective = nextMuted ? 0 : clamp01(nextVolume)
      const p: any = audioPlayer as any

      try {
        if (typeof p?.setVolume === 'function') {
          p.setVolume(effective)
        }
      } catch {}

      try {
        if (typeof p?.setMuted === 'function') {
          p.setMuted(nextMuted)
        }
      } catch {}

      try {
        const a: HTMLAudioElement | undefined = p?.audioRef?.current
        if (a) {
          a.volume = effective
          a.muted = nextMuted
        }
      } catch {}

      if (typeof document !== 'undefined') {
        const els = document.querySelectorAll('audio')
        els.forEach((el) => {
          try {
            ;(el as HTMLAudioElement).volume = effective
            ;(el as HTMLAudioElement).muted = nextMuted
          } catch {}
        })
      }
    },
    [audioPlayer],
  )

  useEffect(() => {
    applyOutputVolume(volume, muted)
  }, [volume, muted, currentSongId, applyOutputVolume])

  const isSongLiked = useMemo(() => {
    if (!currentSongId) return false
    return (favSongs || []).some((s: any) => String(s?._id) === currentSongId)
  }, [favSongs, currentSongId])

  const progress = useMemo(() => {
    if (!duration || duration <= 0) return 0
    return Math.max(0, Math.min(1, currentTime / duration))
  }, [currentTime, duration])

  const controlsDisabled = isLoading
  const canSeek = !isLoading && duration > 0

  const playByIndex = useCallback(
    (idx: number) => {
      if (!safeQueue.length) return
      const clamped = Math.max(0, Math.min(idx, safeQueue.length - 1))
      playQueue(safeQueue, clamped)
    },
    [safeQueue, playQueue],
  )

  const toggleRepeatOne = useCallback(() => {
    if (controlsDisabled) return
    setRepeatMode((m) => (m === 'one' ? 'off' : 'one'))
  }, [controlsDisabled])

  const toggleShuffleNow = useCallback(() => {
    if (controlsDisabled) return
    if (!safeIds.length) return

    const next = !shuffleRef.current
    shuffleRef.current = next
    setShuffle(next)

    if (next) {
      shuffleSigRef.current = queueSig
      shuffleHistoryRef.current = []
      shuffleFutureRef.current = []
      shufflePoolRef.current = fisherYatesShuffle(
        safeIds.filter((id) => id !== currentSongId),
      )
    } else {
      shufflePoolRef.current = []
      shuffleHistoryRef.current = []
      shuffleFutureRef.current = []
      shuffleSigRef.current = ''
    }
  }, [controlsDisabled, safeIds, currentSongId, queueSig])

  useEffect(() => {
    if (!shuffle) {
      shufflePoolRef.current = []
      shuffleHistoryRef.current = []
      shuffleFutureRef.current = []
      shuffleSigRef.current = ''
      return
    }

    if (!safeIds.length || !currentSongId) return

    if (shuffleSigRef.current !== queueSig) {
      shuffleSigRef.current = queueSig
      shuffleHistoryRef.current = []
      shuffleFutureRef.current = []
      shufflePoolRef.current = fisherYatesShuffle(
        safeIds.filter((id) => id !== currentSongId),
      )
    }

    if (!shufflePoolRef.current.length && safeIds.length > 1) {
      shufflePoolRef.current = fisherYatesShuffle(
        safeIds.filter((id) => id !== currentSongId),
      )
    }
  }, [shuffle, queueSig, safeIds, currentSongId])

  useEffect(() => {
    const prev = lastSongIdRef.current
    if (!prev) {
      lastSongIdRef.current = currentSongId
      return
    }
    if (!currentSongId) return

    if (prev !== currentSongId) {
      const cause = navCauseRef.current

      if (shuffle && !cause) {
        shuffleHistoryRef.current = []
        shuffleFutureRef.current = []
        shuffleSigRef.current = queueSig
        shufflePoolRef.current = fisherYatesShuffle(
          safeIds.filter((id) => id !== currentSongId),
        )
      }

      navCauseRef.current = null
      lastSongIdRef.current = currentSongId
    }
  }, [currentSongId, shuffle, queueSig, safeIds])

  const toggleLikeSong = useCallback(async () => {
    if (controlsDisabled) return
    if (!currentSongId) return
    try {
      if (isSongLiked) {
        await dispatch(attemptDeleteSongFromFavs(currentSongId)).unwrap()
      } else {
        await dispatch(attemptAddSongToFavs(currentSongId)).unwrap()
      }
      dispatch(attemptGetFavs())
    } catch {}
  }, [controlsDisabled, currentSongId, isSongLiked, dispatch])

  const goNext = useCallback(() => {
    if (isLoading) return
    if (safeIds.length <= 1) return

    if (shuffleRef.current) {
      let nextId: string | undefined

      if (shuffleFutureRef.current.length) {
        nextId = shuffleFutureRef.current.shift()
      } else {
        if (!shufflePoolRef.current.length) {
          shufflePoolRef.current = fisherYatesShuffle(
            safeIds.filter((id) => id !== currentSongId),
          )
        }
        nextId = shufflePoolRef.current.shift()
      }

      if (!nextId) return

      if (currentSongId) shuffleHistoryRef.current.push(currentSongId)
      navCauseRef.current = 'next'

      const idx = safeIds.findIndex((id) => id === nextId)
      if (idx >= 0) playByIndex(idx)
      return
    }

    if (realIndex < safeIds.length - 1) {
      playByIndex(realIndex + 1)
      return
    }

    if (repeatRef.current === 'all' && safeIds.length > 1) {
      playByIndex(0)
    }
  }, [isLoading, safeIds, currentSongId, realIndex, playByIndex])

  const goPrev = useCallback(() => {
    if (isLoading) return
    if (!safeIds.length) return

    if (shuffleRef.current) {
      const hist = shuffleHistoryRef.current
      if (!hist.length) {
        if (canSeek) seek(0)
        return
      }

      const prevId = hist.pop()
      if (!prevId) return

      if (currentSongId) shuffleFutureRef.current.unshift(currentSongId)
      navCauseRef.current = 'prev'

      const idx = safeIds.findIndex((id) => id === prevId)
      if (idx >= 0) playByIndex(idx)
      return
    }

    if (realIndex > 0) {
      playByIndex(realIndex - 1)
      return
    }

    if (repeatRef.current === 'all' && safeIds.length > 1) {
      playByIndex(safeIds.length - 1)
    }
  }, [isLoading, safeIds, currentSongId, realIndex, playByIndex, canSeek, seek])

  useEffect(() => {
    if (!currentSongId) return
    if (!duration || duration <= 0) return

    const left = duration - currentTime
    const END_EPS = 0.25
    if (left > END_EPS) return

    const now = Date.now()
    const g = autoGuardRef.current
    const recently = g.songId === currentSongId && g.stamp && now - g.stamp < 900
    if (recently) return

    const reallyEnded = left <= 0.12 || currentTime >= duration - 0.12
    if (!reallyEnded) return

    autoGuardRef.current = { songId: currentSongId, stamp: now }

    if (repeatRef.current === 'one') {
      const idx = Math.max(0, Math.min(realIndex, safeQueue.length - 1))
      const wasPlaying = isPlayingRef.current

      playQueue([...safeQueue], idx)

      setTimeout(() => {
        try {
          seek(0)
          if (wasPlaying && !isPlayingRef.current) togglePlayPause()
        } catch {}
      }, 0)

      return
    }

    goNext()
  }, [
    currentSongId,
    currentTime,
    duration,
    seek,
    togglePlayPause,
    goNext,
    safeQueue,
    realIndex,
    playQueue,
  ])

  const handleVolumeChange = (value: number) => {
    const next = clamp01(value)
    setVolume(next)

    if (next <= 0 && !muted) setMuted(true)
    if (next > 0 && muted) setMuted(false)
  }

  const onVolumeRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleVolumeChange(Number(e.target.value))
  }

  const toggleMute = () => {
    if (controlsDisabled) return
    setMuted((m) => !m)
  }

  if (!currentSong) return null

  const volumeProgress = muted ? 0 : volume

  // ===========================
  // MOBILE (portal)
  // ===========================
  const mobileNode = (
    <div className={`gaMobile ${isLoading ? 'is-loading' : ''}`} aria-label="Now playing">
      <div className="gaMobileRow">
        <div className="gaMobileNote" aria-hidden="true">
          <CustomerServiceOutlined />
        </div>

        <div className="gaMobileMeta">
          <div className="gaMobileTitle">{String((currentSong as any)?.title ?? '')}</div>
          <div className="gaMobileArtist">
            {String((currentSong as any)?.artist ?? '')}
          </div>
        </div>

        <button
          className={`gaMobileBtn ${isSongLiked ? 'is-on' : ''}`}
          type="button"
          onClick={toggleLikeSong}
          aria-label="like"
          disabled={controlsDisabled}
        >
          {isSongLiked ? <HeartFilled /> : <HeartOutlined />}
        </button>

        <button
          className="gaMobileBtn gaMobileBtnPlay"
          type="button"
          onClick={togglePlayPause}
          aria-label="play-pause"
          disabled={controlsDisabled}
        >
          {isPlaying ? <PauseOutlined /> : <CaretRightOutlined />}
        </button>
      </div>

      {/* VOLUME (как на твоём 2-м фото) */}
      <div
        className={`gaMobileVol ${controlsDisabled ? 'is-disabled' : ''}`}
        aria-label="volume"
      >
        <button
          className={`gaMobileVolBtn ${muted || volume <= 0 ? 'is-on' : ''}`}
          type="button"
          onClick={toggleMute}
          aria-label={muted || volume <= 0 ? 'Unmute' : 'Mute'}
          disabled={controlsDisabled}
        >
          {muted || volume <= 0 ? <AudioMutedOutlined /> : <SoundOutlined />}
        </button>

        <div className="gaMobileVolBar">
          <div className="gaMobileVolTrack" />
          <div
            className="gaMobileVolFill"
            style={{ width: `${volumeProgress * 100}%` }}
          />
          <input
            className="gaMobileVolRange"
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={volumeProgress}
            onChange={onVolumeRangeChange}
            disabled={controlsDisabled}
            aria-label="Volume"
          />
        </div>
      </div>

      {/* SEEK (как на твоём 1-м фото) */}
      <div
        className={`gaMobileProgress ${isLoading ? 'is-loading' : ''}`}
        aria-label="progress"
      >
        <div className="gaMobileTrack" />
        <div className="gaMobileFill" style={{ width: `${progress * 100}%` }} />
        <input
          className="gaMobileRange"
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={currentTime}
          disabled={!canSeek}
          onChange={(e) => seek(Number(e.target.value))}
          aria-label="Seek"
        />
      </div>
    </div>
  )

  const mobileHost =
    isMobile && typeof document !== 'undefined'
      ? document.getElementById('ga-mobile-slot')
      : null

  if (mobileHost) return createPortal(mobileNode, mobileHost)

  // ===========================
  // DESKTOP (как было)
  // ===========================
  return (
    <div ref={desktopPlayerRef} className={`ga-player ${isLoading ? 'is-loading' : ''}`}>
      <div className="ga-bar">
        <div className="ga-left">
          <button
            className={`ga-btn ga-btn--repeat ${repeatMode !== 'off' ? 'is-on' : ''} ${
              repeatMode === 'one' ? 'is-one' : ''
            }`}
            type="button"
            aria-label="repeat"
            disabled={controlsDisabled}
            onClick={toggleRepeatOne}
            title={repeatMode === 'one' ? 'Repeat one' : 'Repeat off'}
          >
            <span className="gaIconWithBadge">
              <RetweetOutlined />
              {repeatMode === 'one' ? <span className="gaBadge">1</span> : null}
            </span>
          </button>

          <button
            className="ga-btn ga-btn--nav"
            type="button"
            onClick={goPrev}
            aria-label="prev"
            disabled={controlsDisabled}
          >
            <StepBackwardOutlined />
          </button>

          <button
            className={`ga-play ${isLoading ? 'is-loading' : ''}`}
            type="button"
            onClick={togglePlayPause}
            aria-label="play-pause"
            disabled={controlsDisabled}
            title={isLoading ? 'Loading…' : isPlaying ? 'Pause' : 'Play'}
          >
            <span className="ga-play__icon">
              {isLoading ? (
                <LoadingOutlined />
              ) : isPlaying ? (
                <PauseCircleOutlined />
              ) : (
                <PlayCircleOutlined />
              )}
            </span>
          </button>

          <button
            className="ga-btn ga-btn--nav"
            type="button"
            onClick={goNext}
            aria-label="next"
            disabled={controlsDisabled}
          >
            <StepForwardOutlined />
          </button>

          <button
            className={`ga-btn ga-btn--shuffle ${shuffle ? 'is-on' : ''}`}
            type="button"
            onClick={toggleShuffleNow}
            aria-label={shuffle ? 'shuffle on' : 'shuffle off'}
            aria-pressed={shuffle}
            title={shuffle ? 'Shuffle on' : 'Shuffle off'}
            disabled={controlsDisabled}
          >
            <span className="gaIconWithBadge">
              <SwapOutlined />
              {shuffle ? <span className="gaBadge">ON</span> : null}
            </span>
          </button>
        </div>

        <div className="ga-mid">
          <div
            className={`ga-progress ${isLoading ? 'is-loading' : ''}`}
            aria-label="progress"
          >
            <div className="ga-track" />
            <div className="ga-fill" style={{ width: `${progress * 100}%` }} />
            <input
              className="ga-range"
              type="range"
              min={0}
              max={duration || 0}
              step={0.1}
              value={currentTime}
              disabled={!canSeek}
              onChange={(e) => seek(Number(e.target.value))}
            />
          </div>

          <div className="ga-status">
            {isLoading ? (
              <span className="ga-loadingText">Loading track…</span>
            ) : (
              <span className="ga-loadingText" style={{ opacity: 0 }}>
                .
              </span>
            )}
          </div>
        </div>

        <div className="ga-right">
          {/* WEB VOLUME */}
          <div className={`ga-volume ${controlsDisabled ? 'is-disabled' : ''}`}>
            <button
              className={`ga-btn ga-volBtn ${muted || volume <= 0 ? 'is-on' : ''}`}
              type="button"
              onClick={toggleMute}
              aria-label={muted || volume <= 0 ? 'Unmute' : 'Mute'}
              title={muted || volume <= 0 ? 'Unmute' : 'Mute'}
              disabled={controlsDisabled}
            >
              {muted || volume <= 0 ? <AudioMutedOutlined /> : <SoundOutlined />}
            </button>

            <div className="ga-volume__bar" aria-label="volume">
              <div className="ga-volume__track" />
              <div
                className="ga-volume__fill"
                style={{ width: `${volumeProgress * 100}%` }}
              />
              <input
                className="ga-volume__range"
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={volumeProgress}
                onChange={onVolumeRangeChange}
                disabled={controlsDisabled}
                aria-label="Volume"
              />
            </div>
          </div>

          <div className={`ga-cover ${isLoading ? 'is-loading' : ''}`}>
            {(currentSong as any)?.imageUrl ? (
              <img src={(currentSong as any).imageUrl} alt="" />
            ) : (
              <div className="ga-cover__ph" />
            )}
            {isLoading && (
              <div className="ga-cover__spinner" aria-label="loading">
                <LoadingOutlined />
              </div>
            )}
          </div>

          <div className="ga-meta">
            <div className={`ga-title ${isLoading ? 'is-skeleton' : ''}`}>
              {String((currentSong as any)?.title ?? '')}
            </div>
            <div className={`ga-artist ${isLoading ? 'is-skeleton' : ''}`}>
              {String((currentSong as any)?.artist ?? '')}
            </div>
          </div>

          <button
            className={`ga-btn ga-btn--ghost ${isSongLiked ? 'is-on' : ''}`}
            type="button"
            onClick={toggleLikeSong}
            aria-label="like"
            title="Like"
            disabled={controlsDisabled}
          >
            {isSongLiked ? <HeartFilled /> : <HeartOutlined />}
          </button>
        </div>
      </div>

      {queueOpen && <div className="ga-queue" />}
    </div>
  )
}
